import React, { useMemo, useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import Table from "components/Table";
import { actions } from "store/actions";
import Badges from "components/badege";
import { PO_SENT_STATUS, poStatusesOptions } from "utils/constantValues";
import { ReactComponent as CloseSquare } from "images/profile/close-square.svg";

import { ReactComponent as TickSquare } from "images/profile/tick-square.svg";

import { ReactComponent as Document } from "images/profile/document.svg";
import { ReactComponent as AddBox } from "images/profile/box-add.svg";
import { ReactComponent as DangerIcon } from "images/profile/danger.svg";
import PoRespondModal from "pages/projects/common/PoRespondModal";
import TaskBriefPanel from "pages/projects/common/TaskBriefPanel";
import useNotify from "hooks/useNotify";
import { useTranslation } from "react-i18next";
import ActionTooltip from "components/ActionTooltip";
import DeliverablesPanel from "pages/projects/common/DeliverablesPanel";

function AssigneeSubTable({ projectRaw = {}, fetchProjects = () => {} }) {
  const dispatch = useDispatch();

  const [t] = useTranslation();
  const { notifyError, notifySuccess } = useNotify();

  const [isLoading, setIsLoading] = useState(false);
  const userDetails = useSelector((state) => state.userDetails || {});
  const assignees = useSelector((state) => state.assignees || []);

  const [openRespond, setOpenRespond] = useState(false);
  const [respondAcceptance, setRespondAcceptance] = useState(false);
  const [selectedAssigneeId, setSelectedAssigneeId] = useState("");
  const [respondLoading, setRespondLoading] = useState(false);
  const [openTaskBrief, setOpenTaskBrief] = useState(false);
  const [taskBrief, setTaskBrief] = useState("");

  // Deliverables state
  const [isDeliverablesOpen, setDeliverablesOpen] = useState(false);
  const [activeAssignee, setActiveAssignee] = useState(null);

  const subTableColumns = useMemo(
    () => [
      {
        Header: "#PO",
        accessor: "poNumber",
        Cell: ({ value, row }) => {
          const softDeleted = row.original.isSoftDeleted === true;
          return softDeleted ? (
            <div className="inline-flex items-center gap-1.5 !text-red-500">
              <ActionTooltip label={t("poSoftDeletedTooltip")}>
                <DangerIcon
                  className="h-[18px] w-[18px] shrink-0 text-red-500"
                  aria-hidden
                />
              </ActionTooltip>
              <ActionTooltip label={t("downloadPo")}>
                <button
                  type="button"
                  className="font-medium text-[16px] text-red-500 underline cursor-pointer"
                  onClick={() => dispatch(actions.downloadPo(row.original.id))}
                >
                  {value}
                </button>
              </ActionTooltip>
            </div>
          ) : (
            <ActionTooltip label={t("downloadPo")}>
              <button
                type="button"
                className="font-medium text-[#1D1B22] text-[16px] underline cursor-pointer"
                onClick={() => dispatch(actions.downloadPo(row.original.id))}
              >
                {value}
              </button>
            </ActionTooltip>
          );
        },
      },
      {
        Header: "Task Status",
        accessor: "status",
        Cell: ({ value }) => {
          return (
            <Badges
              label={poStatusesOptions[value]?.label ?? value}
              variant={poStatusesOptions[value]?.variant ?? "primary"}
            />
          );
        },
      },
      {
        Header: "Assignee Name",
        accessor: "assigneeName",
        Cell: ({ value }) => <div className="text-[#1D1B22]">{value}</div>,
      },
      {
        Header: t("fileNameHeader"),
        accessor: "parentFile.name",
        Cell: ({ row }) => {
          const parentFile = row?.original?.parentFile;
          if (!parentFile?.name) return <div className="text-[#666666]">-</div>;
          return (
            <ActionTooltip label={parentFile?.name ?? ""}>
              <button
                className={`font-medium text-[#1D1B22] text-[16px] ${
                  parentFile.id ? "underline cursor-pointer" : "cursor-default"
                } max-w-[220px] truncate text-left`}
                title={parentFile.name}
                onClick={() => {
                  if (!parentFile?.id) return;
                  dispatch(actions.downloadParentFile(parentFile.id));
                }}
              >
                {parentFile.name}
              </button>
            </ActionTooltip>
          );
        },
      },
      {
        Header: "Batch Volume",
        accessor: "unitCount",
        Cell: (props) => {
          const unitCount = props.data[props.row.id]?.unitCount;
          const uom = projectRaw?.uom;
          return <div className="text-[#1D1B22]">{`${unitCount} ${uom}`}</div>;
        },
      },
      {
        Header: "Deadline",
        accessor: "deadline",
        Cell: ({ value }) => {
          if (!value) return <div className="text-[#666666]">-</div>;

          const date = new Date(value);
          const formattedDate = date.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          });
          const formattedTime = date.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          });

          return (
            <div className="text-[#1D1B22]">
              <div className="font-medium">{formattedDate}</div>
              <div className="text-[12px] text-[#666666]">{formattedTime}</div>
            </div>
          );
        },
      },

      {
        id: "action",
        Header: "",
        Cell: ({ row }) => (
          <div className="flex items-center gap-[15px]">
            {row.original.workSpaceUrl && (
              <ActionTooltip label={t("workSpace")}>
                <div
                  className="flex py-[2px] px-[6px] items-center gap-[6px] border rounded-[6px] border-[#1E4A6B] bg-[#fff] cursor-pointer"
                  onClick={() =>
                    window.open(row.original.workSpaceUrl, "_blank")
                  }
                >
                  <div className="text-[#1E4A6B] text-[14px] font-semibold mt-[2px]">
                    {t("workSpace")}
                  </div>
                </div>
              </ActionTooltip>
            )}
            {row.original.taskBrief && (
              <ActionTooltip label={t("viewTaskBrief")}>
                <button
                  title={t("viewTaskBrief")}
                  onClick={() => {
                    setOpenTaskBrief(true);
                    setTaskBrief(row.original.taskBrief);
                  }}
                >
                  <Document />
                </button>
              </ActionTooltip>
            )}
            <ActionTooltip label={t("uploadDeliverables")}>
              <button
                onClick={() => {
                  setActiveAssignee(row.original);
                  setDeliverablesOpen(true);
                }}
              >
                <AddBox />
              </button>
            </ActionTooltip>

            {PO_SENT_STATUS === row.original.status && (
              <>
                <ActionTooltip label={t("acceptPo")}>
                  <button
                    onClick={() => {
                      setSelectedAssigneeId(row.original.assigneeId);
                      setRespondAcceptance(true);
                      setOpenRespond(true);
                    }}
                  >
                    <TickSquare />
                  </button>
                </ActionTooltip>

                <ActionTooltip label={t("rejectPo")}>
                  <button
                    onClick={() => {
                      setSelectedAssigneeId(row.original.assigneeId);
                      setRespondAcceptance(false);
                      setOpenRespond(true);
                    }}
                  >
                    <CloseSquare />
                  </button>
                </ActionTooltip>
              </>
            )}
          </div>
        ),
      },
    ],
    [dispatch, projectRaw?.uom, t]
  );

  const fetchAssignees = useCallback(() => {
    setIsLoading(true);
    dispatch(
      actions.getAssigneesPerProject(
        userDetails?.agencyId,
        projectRaw?.id,
        (success) => {
          setIsLoading(false);
          if (!success) {
            console.error("Failed to fetch projects");
          }
        }
      )
    );
  }, [dispatch, userDetails?.agencyId, projectRaw?.id]);

  const resetUnMount = () => {};

  useEffect(() => {
    if (projectRaw.id) {
      fetchAssignees();
    }
  }, [projectRaw.id, fetchAssignees]);

  useEffect(() => {
    return () => {
      resetUnMount();
    };
  }, []);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="text-gray-500">{t("loadingAssignee")}</div>
      </div>
    );
  }
  return (
    <>
      <Table columns={subTableColumns} data={assignees} subTable={true} />

      <PoRespondModal
        title={t("poRespondTitle")}
        isOpen={openRespond}
        onClose={() => setOpenRespond(false)}
        onSubmit={(assigneeId, acceptance) => {
          setRespondLoading(true);
          dispatch(
            actions.respondToPo(assigneeId, acceptance, (success, err) => {
              setRespondLoading(false);
              if (success) {
                fetchProjects();
                setOpenRespond(false);
                notifySuccess(acceptance ? t("poAcceptMsg") : t("rejectTask"));
              } else {
                notifyError(
                  err?.response?.data?.message ??
                    err?.data?.error ??
                    err?.message ??
                    err?.error ??
                    "Something went wrong"
                );
              }
            })
          );
        }}
        isLoading={respondLoading}
        acceptance={respondAcceptance}
        assigneeId={selectedAssigneeId}
      />

      <TaskBriefPanel
        isOpen={openTaskBrief}
        onClose={() => setOpenTaskBrief(false)}
        brief={taskBrief}
        deadline={projectRaw?.deadline}
      />

      <DeliverablesPanel
        activeAssignee={activeAssignee}
        isOpen={isDeliverablesOpen}
        onClose={() => setDeliverablesOpen(false)}
        projectId={projectRaw?.id}
      />
    </>
  );
}

export default AssigneeSubTable;
